import type { DishTag } from "api-types";

import React from "react";

import { Box, Chip, Link, Stack, useTheme } from "@mui/material";

import {
  CancelRounded,
  CheckBox,
  CheckBoxOutlineBlank,
  CloseRounded,
  RadioButtonCheckedRounded,
  RadioButtonUncheckedRounded,
} from "@mui/icons-material";

import { InfoIcon } from "../Icons";
import AllergyIcon from "../Icons/Allergy";
import CheckboxIcon from "../Icons/Checkbox";
import CheckboxCheckedIcon from "../Icons/CheckboxChecked";
import TickIcon from "../Icons/Tick";
import TrashIcon from "../Icons/Trash";
import { SpiceLevelIcon } from "../SpiceLevelIcon/SpiceLevelIcon";
import { StyledTooltip } from "../tooltip/Tooltip";

export const baseChipStyle = {
  borderTopLeftRadius: 25,
  borderBottomLeftRadius: 0,
  paddingLeft: 1,
  paddingRight: 1,
  minHeight: 32,
  maxHeight: 32,
  height: 32,
};

/** Used for showing selectable allergy items.
 * If there is no onClick, the chip will show no hover or click effect */
export const AllergenOptionalChip = ({
  allergenName,
  description,
  onClick,
  onDelete,
  selected = false,
  isOptional = false,
  showOptionalCheckbox = false,
}: {
  allergenName: string;
  description?: string;
  onClick?: () => void;
  onDelete?: () => void;
  selected?: boolean;
  isOptional?: boolean;
  /** Render a small leading checkbox indicating the allergen's "optional" state. */
  showOptionalCheckbox?: boolean;
}) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();
  return (
    <StyledTooltip infoIcon title={description} placement="top">
      <Chip
        icon={
          showOptionalCheckbox ? (
            isOptional ? (
              <CheckboxCheckedIcon
                color={oscar.light.button.secondary}
                height={14}
                width={14}
              />
            ) : (
              <CheckboxIcon
                color={oscar.light.button.dark}
                height={14}
                width={14}
              />
            )
          ) : undefined
        }
        deleteIcon={
          onDelete ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                alignSelf: "center",
              }}
            >
              {selected ? (
                <CheckBox
                  sx={({ palette }) => ({ fill: palette.allergy.main })}
                />
              ) : (
                <CheckBoxOutlineBlank
                  sx={({ palette }) => ({ fill: palette.allergy.main })}
                />
              )}
            </div>
          ) : (
            <div />
          )
        }
        color={selected ? "error" : "default"}
        label={isOptional ? `*${allergenName}` : allergenName}
        sx={{
          "&&": {
            backgroundColor: isOptional
              ? oscar.light.surface.gray
              : oscar.light.surface.danger,
          },
          ...baseChipStyle,
          ...typography.labelSMedium,
          color: isOptional
            ? oscar.light.content.content1
            : oscar.light.content.dangerText,
          "& .MuiChip-icon": {
            marginLeft: 0.5,
            marginRight: -0.25,
          },
        }}
        onClick={onClick}
        onDelete={onDelete}
      />
    </StyledTooltip>
  );
};

/** Used for showing selectable allergy items.
 * If there is no onClick, the chip will show no hover or click effect */
export const AllergenChip = ({
  allergenName,
  description,
  selected,
  hasAllergenIcon = true,
  hasActiveColor,
  enabled = true,
  onClick,
  onDelete,
  action,
  dataDDActionName,
  isOptional = false,
  mayContain = false,
}: {
  allergenName: React.ReactNode;
  description?: string;
  onClick?: () => void;
  onDelete?: () => void;
  hasAllergenIcon?: boolean;
  hasActiveColor?: boolean;
  enabled?: boolean;
  selected?: boolean;
  action?: "delete" | "select";
  dataDDActionName?: string;
  isOptional?: boolean;
  /**
   * Cross-contact rather than an ingredient: the dashed outline this draws is
   * the house glyph for it, matching AllergenChipOutlined. Kept selectable,
   * which is the one thing that component cannot do.
   */
  mayContain?: boolean;
}) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();
  return (
    <StyledTooltip infoIcon title={description} placement="top">
      <Chip
        icon={
          hasAllergenIcon ? (
            <AllergyIcon
              color={
                selected || hasActiveColor
                  ? oscar.light.content.danger
                  : oscar.light.icon.dark
              }
              height={12}
              width={12}
            />
          ) : undefined
        }
        sx={({ palette }) => ({
          "&&": {
            borderColor: mayContain
              ? palette.oscar.light.content.danger
              : palette.oscar.light.surface.danger,
            borderWidth: mayContain ? 1 : 0,
            backgroundColor: mayContain
              ? oscar.light.surface.clear
              : isOptional
              ? oscar.light.surface.gray
              : selected || hasActiveColor
              ? oscar.light.surface.danger
              : oscar.light.surface.neutral,
            borderStyle: mayContain ? "dashed" : "solid",
          },
          ...baseChipStyle,
          ...typography.labelSMedium,
          color: !enabled
            ? palette.grey[700]
            : mayContain
            ? oscar.light.content.dangerText
            : isOptional
            ? oscar.light.content.content1
            : selected || hasActiveColor
            ? oscar.light.content.dangerText
            : oscar.light.content.content1,
        })}
        label={isOptional ? `*${allergenName}` : allergenName}
        // A selectable chip has a state, and a screen reader had no way to know
        // it: the checkbox glyph carried the whole answer visually.
        aria-pressed={action === "select" ? selected : undefined}
        onClick={() => (enabled ? onClick?.() : {})}
        // MUI stops propagation on the delete slot, so in select mode the
        // checkbox, the strongest tap target on the chip, reached nothing and
        // only the label text toggled.
        onDelete={
          enabled && onDelete
            ? action === "select"
              ? () => onClick?.()
              : () => onDelete?.()
            : undefined
        }
        deleteIcon={
          <>
            {!action || action === "delete" ? (
              <TrashIcon
                color={oscar.light.content.danger}
                height={14}
                width={14}
              />
            ) : null}
            {action === "select" ? (
              <>
                {selected ? (
                  <CheckboxCheckedIcon
                    color={
                      isOptional
                        ? oscar.light.content.content2
                        : oscar.light.content.danger
                    }
                    height={16}
                    width={16}
                  />
                ) : (
                  <CheckboxIcon
                    color={
                      isOptional
                        ? oscar.light.content.content2
                        : hasActiveColor
                        ? oscar.light.content.danger
                        : oscar.light.button.dark
                    }
                    height={16}
                    width={16}
                  />
                )}
              </>
            ) : null}
          </>
        }
        data-dd-action-name={dataDDActionName}
      />
    </StyledTooltip>
  );
};
// ) : (
//   <CheckBoxOutlineBlank fontSize={"small"} />
// )

/** Used for showing selectable preference items.
 * If there is no onClick, the chip will show no hover or click effect */
export const PreferenceChip = ({
  preferenceName,
  description,
  includeIcon = true,
  onClick,
  onDelete,
  action,
  selected = false,
  dataDDActionName,
}: {
  preferenceName: string;
  description?: string;
  includeIcon?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  selected?: boolean;
  action?: "delete" | "select";
  dataDDActionName?: string;
}) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();
  return (
    <StyledTooltip infoIcon title={description} placement="top">
      <Chip
        icon={
          includeIcon ? (
            <TickIcon
              color={
                selected ? oscar.light.icon.approval : oscar.light.icon.dark
              }
              height={12}
              width={12}
            />
          ) : undefined
        }
        sx={{
          "&&": {
            backgroundColor: selected
              ? oscar.light.surface.safe
              : oscar.light.surface.neutral,
          },
          ...baseChipStyle,
          ...typography.labelSMedium,
          color: selected
            ? oscar.light.content.success
            : oscar.light.content.content1,
        }}
        label={preferenceName}
        onClick={onClick}
        onDelete={onDelete}
        deleteIcon={
          <>
            {!action || action === "delete" ? (
              <TrashIcon
                color={oscar.light.content.success}
                height={14}
                width={14}
              />
            ) : null}
            {action === "select" ? (
              <>
                {selected ? (
                  <CheckboxCheckedIcon
                    color={oscar.light.content.success}
                    height={16}
                    width={16}
                  />
                ) : (
                  <CheckboxIcon
                    color={oscar.light.icon.dark}
                    height={16}
                    width={16}
                  />
                )}
              </>
            ) : null}
          </>
        }
        data-dd-action-name={dataDDActionName}
      />
    </StyledTooltip>
  );
};

/** Used for showing selectable preference items.
 * If there is no onClick, the chip will show no hover or click effect */
export const TagSelectableChip = ({
  tag,
  includeIcon = true,
  action,
  selected,
  ...rest
}: {
  tag: DishTag;
  includeIcon?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  selected?: boolean;
  action?: "delete" | "select";
  dataDDActionName?: string;
}) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();
  if (tag.displayIntent === "recommend") {
    return (
      <PreferenceChip
        {...rest}
        action={action}
        selected={selected}
        includeIcon={includeIcon}
        preferenceName={tag.name}
        description={tag.description}
      />
    );
  }
  if (tag.displayIntent === "avoid") {
    return (
      <AllergenChip
        {...rest}
        action={action}
        selected={selected}
        allergenName={tag.name}
        description={tag.description}
      />
    );
  }
  return (
    <StyledTooltip infoIcon title={tag.description} placement="top">
      <Chip
        icon={
          <TickIcon
            color={selected ? oscar.light.tag.content : oscar.light.icon.dark}
            height={12}
            width={12}
          />
        }
        deleteIcon={
          <>
            {!action || action === "delete" ? (
              <TrashIcon
                color={oscar.light.tag.content}
                height={14}
                width={14}
              />
            ) : null}
            {action === "select" ? (
              <>
                {selected ? (
                  <CheckboxCheckedIcon
                    color={oscar.light.tag.content}
                    height={16}
                    width={16}
                  />
                ) : (
                  <CheckboxIcon
                    color={oscar.light.content.content1}
                    height={16}
                    width={16}
                  />
                )}
              </>
            ) : null}
          </>
        }
        sx={{
          "&&": {
            backgroundColor: selected
              ? oscar.light.tag.background
              : oscar.light.surface.neutral,
          },
          color: selected
            ? oscar.light.tag.content
            : oscar.light.content.content1,
          ...baseChipStyle,
          ...typography.labelSMedium,
        }}
        label={tag.name}
        onClick={rest.onClick}
        onDelete={rest.onDelete}
        data-dd-action-name={rest.dataDDActionName}
      />
    </StyledTooltip>
  );
};

/** Used for showing non selectable allergy items. */
export const AllergenChipOutlined = ({
  allergenName,
  description,
  link,
  canOmit = false,
  mayContain = false,
  onClick,
}: {
  allergenName: string;
  description?: string;
  link?: string;
  canOmit?: boolean;
  mayContain?: boolean;
  onClick?: () => void;
}) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();
  return (
    <StyledTooltip infoIcon title={description} placement="top">
      <Chip
        sx={({ palette }) => ({
          "&&": {
            borderColor: mayContain
              ? palette.oscar.light.content.danger
              : palette.oscar.light.surface.danger,
            borderWidth: mayContain || canOmit ? 1 : 0,
            backgroundColor: mayContain
              ? palette.oscar.light.surface.clear
              : palette.oscar.light.surface.danger,
            borderStyle: mayContain ? "dashed" : "solid",
          },
          ...baseChipStyle,
          ...typography.labelSMedium,
          color: palette.oscar.light.content.dangerText,
        })}
        deleteIcon={
          <>
            {onClick ? (
              <Stack
                sx={{
                  cursor: "pointer",
                  backgroundColor: oscar.light.surface.danger,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={onClick}
              >
                <CloseRounded sx={{ fontSize: 12 }} />
              </Stack>
            ) : null}
          </>
        }
        onDelete={onClick}
        color={"default"}
        label={
          <>
            {allergenName}
            {link && (
              <Link
                sx={{
                  ...typography.labelSMedium,
                  color: oscar.light.content.link,
                  cursor: "pointer",
                  paddingLeft: 1,
                }}
                underline="none"
              >
                {link}
              </Link>
            )}
          </>
        }
        variant="outlined"
      />
    </StyledTooltip>
  );
};

/** Used for showing non selectable tag items. */
export const TagChip = ({
  tag,
  size,
}: {
  tag: DishTag;
  size?: "small" | "medium";
}) => {
  if (tag.displayIntent === "recommend") {
    return (
      <PreferenceChipOutlined
        preferenceName={tag.name}
        description={tag.description}
        size={size}
      />
    );
  }
  if (tag.displayIntent === "avoid") {
    return (
      <AllergenChipOutlined
        allergenName={tag.name}
        description={tag.description}
      />
    );
  }
  return (
    <StyledTooltip infoIcon title={tag.description} placement="top">
      <Chip
        size={size}
        sx={(theme) => ({
          "&&": {
            borderWidth: 0,
          },
          fontSize: 12,
          backgroundColor: theme.palette.grey[300],
          borderTopLeftRadius: 30,
          borderBottomLeftRadius: 0,
          paddingLeft: 1,
        })}
        color={"default"}
        label={tag.name}
        variant={"outlined"}
      />
    </StyledTooltip>
  );
};

/** Used for showing non selectable preference items. */
export const PreferenceChipOutlined = ({
  preferenceName,
  description,
  size,
  link,
}: {
  preferenceName: string;
  description?: string;
  link?: string;
  size?: "small" | "medium";
}) => {
  const {
    palette: { oscar },
    typography,
  } = useTheme();
  return (
    <StyledTooltip infoIcon title={description} placement="top">
      <Chip
        size={size}
        sx={{
          "&&": {
            borderColor: "accent",
            borderWidth: 0,
            backgroundColor: oscar.light.surface.safe,
            borderStyle: "solid",
          },
          ...baseChipStyle,
          ...typography.labelSMedium,
          color: oscar.light.content.success,
        }}
        color={"default"}
        label={
          <>
            {preferenceName}
            {link && (
              <Link
                sx={{
                  ...typography.labelSMedium,
                  color: oscar.light.content.link,
                  cursor: "pointer",
                  paddingLeft: 1,
                }}
                underline="none"
              >
                {link}
              </Link>
            )}
          </>
        }
        variant={"outlined"}
      />
    </StyledTooltip>
  );
};

/** Used for showing price range items. */
export const PriceRangeChip = ({
  repeatValue,
  currencySymbol,
  onClick,
  selected = false,
}: {
  repeatValue: number;
  currencySymbol: string;
  onClick?: () => void;
  selected?: boolean;
}) => {
  return (
    <Chip
      icon={
        // One of these is picked at a time, so both states are radios. A
        // square for the off state read as though several could be.
        selected ? (
          <RadioButtonCheckedRounded
            sx={{ "&&": { color: "text.primary", fontSize: "16px" } }}
          />
        ) : (
          <RadioButtonUncheckedRounded
            sx={{ "&&": { color: "text.primary", fontSize: "16px" } }}
          />
        )
      }
      label={currencySymbol.repeat(repeatValue > 0 ? repeatValue : 1)}
      onClick={onClick}
    />
  );
};

/** Used for showing Spice Level. */
export const SpiceLevelChip = ({
  repeatValue,
  label,
  onClick,
  selected = false,
}: {
  repeatValue: number;
  label: string;
  onClick?: () => void;
  selected?: boolean;
}) => {
  return (
    <Box>
      <Chip
        icon={
          selected ? (
            <RadioButtonCheckedRounded
              sx={{ "&&": { color: "text.primary", fontSize: "16px" } }}
            />
          ) : (
            <RadioButtonUncheckedRounded
              sx={{ "&&": { color: "text.primary", fontSize: "16px" } }}
            />
          )
        }
        label={
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
          >
            {label}
            <SpiceLevelIcon level={repeatValue} />
          </Box>
        }
        onClick={onClick}
        // Chip's own label is 13px, which is off the scale everything else in
        // the drawer is set to and leaves the radio reading large beside it.
        sx={{ ".MuiChip-label": { fontSize: "14px" } }}
      />
    </Box>
  );
};

/** Used for showing cuisine items. */
export const CuisineChip = ({
  cuisineName,
  onDelete,
}: {
  cuisineName: string;
  onDelete?: () => void;
}) => {
  return (
    <Chip
      variant={"outlined"}
      deleteIcon={
        <CancelRounded
          fontSize={"small"}
          sx={{ "&&": { color: "grey.A300" } }}
        />
      }
      label={cuisineName}
      onDelete={onDelete}
    />
  );
};
